// How a write behaves — TODO T18 (31 Aug 2026), given its own file at T30 (1 Sep 2026).
//
// Every write in the brain goes through here, and every write ends by REFETCHING rather than by
// patching the cache.
//
// That is the polling model applied to its own mutations (CLAUDE.md § Brain tier: "polling not
// sockets"). An optimistic cache update would be a second implementation of what each endpoint
// does — the pipeline upsert's `changed_at`, the generated code, the consent's `captured_by` —
// written in the browser, from the same guesses, and wrong in exactly the cases that matter. A
// refetch costs one request on an action a human took, and it shows what the database holds.
import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { useRequest } from './provider.tsx';
import type { RequestFn } from './core.ts';

/**
 * `invalidates` is a list of query-key PREFIXES. Broad on purpose: naming precisely which lists a
 * stage change affects is the kind of bookkeeping that is right on the day it is written.
 *
 * A button stays pending until what it changed is on screen: the write settles once the refetch has.
 */
export function useConsoleMutation<TArgs, TResult>(
  run: (request: RequestFn, args: TArgs) => Promise<TResult>,
  invalidates: readonly (readonly unknown[])[],
  options: {
    /** Runs when the write succeeds, mounted or not (a `mutate` call's own callbacks need the component). */
    onWritten?: (args: TArgs) => void;
    /** Runs when the write fails, mounted or not, with what failed. */
    onError?: (args: TArgs, error: unknown) => void;
  } = {},
): UseMutationResult<TResult, unknown, TArgs> {
  const request = useRequest();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: TArgs) => run(request, args),
    ...(options.onError
      ? { onError: (error: unknown, args: TArgs) => options.onError?.(args, error) }
      : {}),
    onSuccess: async (_result: TResult, args: TArgs) => {
      options.onWritten?.(args);
      await Promise.all(invalidates.map((queryKey) => queryClient.invalidateQueries({ queryKey })));
    },
  });
}

/**
 * The key groups the domain modules invalidate against.
 *
 * They live here rather than in each module because they CROSS domains — putting a candidate on a
 * search moves a search list, a candidate list and a candidate — and a group defined twice is a
 * screen that quietly stops refreshing. A new domain adds its group at the slot line below.
 */
export const KEYS = {
  /** What a write to a search touches: the list, the mandate, and its candidates. */
  search: [['searches'], ['search'], ['candidates'], ['candidate'], ['roster']],
  /**
   * A client write (T58): the three CRM tabs and their counts on the nav, the company, the stage
   * list's per-stage numbers, and the searches — whose "live" follows the company's tab.
   */
  client: [
    ['clients'],
    ['client'],
    // T62: a contact's consent is recorded on their own page too.
    ['contact'],
    ['counts'],
    ['stages'],
    ['searches'],
    ['search'],
    ['roster'],
    // T330: a company deleted comes off every outreach list it was on.
    ['talent-clients'],
    ['talent-client'],
  ],
  /** The team's stage list (T58): the list, and every company that shows a stage name. */
  stage: [['stages'], ['clients'], ['client'], ['talent-clients'], ['talent-client']],
  /**
   * T330: a person we are talent agent for, or their outreach list: both boards, their pages, the
   * company pages that name them, and the column counts.
   */
  talent: [['talent-clients'], ['talent-client'], ['clients'], ['client'], ['stages'], ['counts']],
  /**
   * T430: a person's companies, their contact link or whom kadmon talks to them as: the candidate
   * page, the contact's company page and the card's, the board's counts and the Clients tabs.
   */
  people: [
    ['candidate'],
    ['candidates'],
    ['client'],
    ['clients'],
    ['contact'],
    ['talent-clients'],
    ['talent-client'],
    ['stages'],
    ['counts'],
  ],
  // T273: Candidates → Proactive's stages move with a pick's answer too.
  candidate: [['candidate'], ['candidates'], ['proactive'], ['tags']],
  // T52a: a client contact's code is issued and sent from their own page.
  code: [['codes'], ['candidate'], ['contact']],
  /**
   * Clearing an opt-out (T18c) refetches the thread and the conversations list, because the
   * answer to "did it work" is the thread's own `optedOut` flag going false once the worker has
   * run — nothing here knows it yet.
   */
  optOut: [['conversation'], ['conversations']],
  /** Marking the feed read (T36) moves the unread count in the counts poll too. */
  activity: [['activity'], ['counts']],

  /**
   * A question asked of kadmon (T55). The chat it was asked in — its turns are the answer — and the
   * list, whose order is last activity and whose newest row may be the chat this ask just opened.
   */
  chat: [['chat'], ['chats']],

  /**
   * A confirm card applied (T56; every brain write but a delete since T68). kadmon may have
   * written almost anything the brain can — a note, a rating, a stage, a Search, a contact, a
   * code, a hold, a send request, a clearance, a consent, a reference, a research decision — so
   * this is the union of every other group in this file (`mutation.test.ts` holds it), and the
   * chat the card is drawn in, where the outcome shows. Broad on purpose: one tap, every list
   * that could have moved.
   */
  action: [
    ['chat'],
    ['chats'],
    ['candidate'],
    ['candidates'],
    ['proactive'],
    ['candidateEntries'],
    ['searches'],
    ['search'],
    ['roster'],
    ['tags'],
    ['codes'],
    ['clients'],
    ['client'],
    // T330: a card's company, deleted or moved by a card the assistant proposed.
    ['talent-clients'],
    ['talent-client'],
    ['hold'],
    ['conversation'],
    ['conversations'],
    ['outbound'],
    ['held-words'],
    ['thread'],
    ['profile'],
    ['activity'],
    ['counts'],
    ['contact'],
    ['stages'],
    ['teamMeeting'],
    ['teamMeetings'],
    ['verification'],
    // T139: the grades card; a grade card (T139c) changes it.
    ['grades'],
    ['research'],
    ['compare'],
    ['meetings'],
    ['meeting'],
    ['fathom'],
    // T159: a card can disconnect the recruiter's mailbox.
    ['google'],
    // T176a: a card can keep a line in the recruiter's own note.
    ['note'],
    // T570a: `set_appearance` saves their theme and accent, so the asking tab shows it at once.
    ['appearance'],
    // T328: the list is the agent's to change (T328c's tools act at once); the group is here so the card view keeps up.
    ['list'],
    ['waiting'],
    ['proposals'],
    // T166: a card can answer kadmon asking to meet a person (`decide_meeting`).
    ['meeting-ask'],
    // T177: her questions to the team, and a Search's known answers with them.
    ['team-questions'],
    // T111: the Connections tab — kadmon's cards reach every control on it.
    ['connections'],
    ['connection'],
    ['connectionStages'],
    ['connectionUploads'],
    ['clientConnections'],
  ],

  /**
   * Pausing or resuming a thread (T19). The hold itself, plus the two lists that render it: the
   * mandate's candidate list carries a Paused badge already (T18's `SearchCandidate.hold`), and
   * `['search']` is the prefix both it and the mandate share. `hold` is keyed per conversation and
   * this is a prefix too, so one press refetches the thread being looked at and no other.
   */
  hold: [['hold'], ['conversation'], ['search'], ['waiting'], ['counts']],

  /**
   * The demo's Start (T156): nothing on screen changes until her message lands, which the thread
   * poll brings a few seconds later; the thread is refetched so the wait starts at once.
   */
  demoStart: [['thread']],

  /**
   * T166: a yes or a no to kadmon asking to meet the thread's person. The ask shows it on its way
   * at once; her message telling them arrives through the thread poll once the worker has sent it.
   */
  meetingAsk: [['meeting-ask'], ['thread'], ['activity'], ['counts']],

  /**
   * T177: an answer to one of kadmon's questions for the team. The question shows it on its way at
   * once; an answer about the Search is read from the question (T202 S-1), so the Search's page lists it too.
   */
  teamQuestion: [['team-questions'], ['thread'], ['activity'], ['counts'], ['search']],

  /**
   * Resume-and-send (T71): a hold cleared AND a send request filed in one press, so both groups'
   * screens — and the two places the thread stops being "waiting on you": the card and the nav's
   * count.
   */
  release: [
    ['hold'],
    ['outbound'],
    ['thread'],
    ['conversation'],
    ['conversations'],
    ['search'],
    ['waiting'],
    ['counts'],
  ],

  /**
   * Sending or withdrawing a message (T19a). The requests poll, so `pending` appears at once;
   * the thread, so the human bubble lands the moment the worker has sent it rather than a poll
   * later; and the conversation, whose "last outbound" moves with it. Since T423 a send also
   * clears a pause (the hold), which moves the Search's and the nav's waiting counts.
   */
  outbound: [
    ['outbound'],
    ['held-words'],
    ['thread'],
    ['conversation'],
    ['hold'],
    ['search'],
    ['waiting'],
    ['counts'],
  ],

  /**
   * A check or a reference (T33). `['verification']` is the card's own key, and `['candidate']`
   * is here because the identity header's summary badge is drawn from the same record — the two
   * are one read, so one press moves both.
   */
  verification: [['verification'], ['candidate']],

  /**
   * A research decision (T53). The run and the list share one prefix; a decision also touches the
   * candidate lists, because an accept becomes a record and a placement a few seconds later, and
   * the search, whose people an "Add to this Search" moves.
   */
  research: [['research'], ['candidates'], ['candidate'], ['search'], ['searches']],

  /**
   * A meeting filed under a person (T70): the meeting itself, every list of meetings — the
   * person's card, the company's tab, Unfiled — and nothing else, since the meeting is the sync's.
   */
  meeting: [['meeting'], ['meetings']],

  /** The recruiter's Fathom card (T70): connected or not, and the sync's verdict on the key. */
  fathom: [['fathom']],

  /** The recruiter's mailbox card (T159): connected or not, and to which Google account. */
  google: [['google']],
  /** T176a: the recruiter's own note, which Settings shows and edits. */
  note: [['note']],
  /** T570a: their theme and accent, saved to the account and polled by every open tab. */
  appearance: [['appearance']],
  /** T328: their own list: ticking, adding, editing, deleting and putting back all move the one list. */
  list: [['list']],

  /** A 1-on-1 shared or taken back (T70b): the meeting's own page, and the Team tab's list. */
  teamMeeting: [['teamMeeting'], ['teamMeetings']],

  /**
   * A meeting edited or deleted (T70e): its own page, and every list it can be on — a person's,
   * a company's, Nomads, Demos / providers, the Team tab and its index.
   */
  meetingChange: [['meeting'], ['meetings'], ['teamMeeting'], ['teamMeetings']],

  /**
   * A contact lookup (T53a), started or used. The candidate page and the Research tab draw the
   * lookup, and a Use files an entry request — the trail gains a line, and on a proposal the Accept
   * moves the run. Not `['contactLookup']` itself: the poll refetches these when it has its answer,
   * and a poll that invalidated itself would never rest.
   */
  contactLookup: [
    ['candidate'],
    ['candidates'],
    ['candidateEntries'],
    ['research'],
    // T468: a client contact's page draws the same strip.
    ['contact'],
    // T554b: the put-forward dialog and a client document's Send read their contacts from the client.
    ['client'],
  ],

  /**
   * A filed entry request (T38). It invalidates the candidate keys because the worker applies it
   * a second or two later and the record they show is what changes — and `candidateEntries`
   * because the trail gains a line the moment the request exists, before it is applied.
   */
  candidateEntry: [['candidate'], ['candidates'], ['candidateEntries']],

  /**
   * A captured fact applied or dismissed (T48). The candidate, because the suggestion goes the
   * moment its review row exists and the record follows once the worker has applied the request;
   * the entry trail, which gains a line on an Apply.
   */
  factReview: [['candidate'], ['candidates'], ['candidateEntries']],

  /**
   * A grade's correction or an objection to grading (T139): the grades card, and the candidate
   * page, whose entry trail gains the objection's request.
   */
  grades: [['grades'], ['candidate'], ['candidateEntries']],

  /**
   * A rating (T41). One helper in the service reads the latest row for the candidate header, the
   * search list's column, the comparison's small ring and the client roster — so one press
   * refetches all four rather than leaving three stale numbers on screen.
   */
  rating: [['candidate'], ['search'], ['compare'], ['roster']],

  /**
   * A put-forward (T42). The mandate row on the candidate page, the search list's row, the
   * roster, the profile it creates, and the feed line it writes; `['candidates']` because the
   * stage it moves shows on the list too.
   */
  submission: [
    ['candidate'],
    ['candidates'],
    ['search'],
    ['roster'],
    ['profile'],
    ['activity'],
    // T62: withdrawing a put-forward takes its message off the contact's page.
    ['contact'],
  ],

  /**
   * The client's message (T62): asked for, edited or dismissed on the contact's page, which is
   * the one read that shows it.
   */
  draft: [['contact']],

  /**
   * The Proposed list (T91): an ask, an approval or a rejection. The list itself; and, because
   * an approval IS a put-forward with the client's message opened, everything a put-forward
   * moves — the person's stage on the Search, the roster, their profile, the feed — and the
   * contact's page the message lands on. The tick-box in the approve dialog records a consent,
   * which the candidate's page shows. And the three numbers that say how many are waiting: the
   * side menu's (`counts`), the company's card on the board (`clients`) and its tab's label
   * (`client`) — a badge still reading "2 waiting" after the second approval is a lie.
   */
  proposal: [
    ['proposals'],
    ['counts'],
    ['clients'],
    ['client'],
    ['candidate'],
    ['candidates'],
    ['search'],
    ['searches'],
    ['roster'],
    ['profile'],
    ['activity'],
    ['contact'],
  ],

  /**
   * A card sent on kadmon's number (T52a): the page it is on, and the thread it goes out on — its
   * pause, its requests poll and its messages, which the worker moves a second or two later.
   */
  draftSend: [['contact'], ['hold'], ['outbound'], ['thread'], ['conversation']],

  /**
   * An import (T43): the candidates it files and their trails, the clients and roles it creates,
   * the rosters its placements move, and the shell's counts. Broad because one confirm touches
   * every list in the app at once.
   */
  import: [
    ['candidates'],
    ['candidate'],
    ['candidateEntries'],
    ['clients'],
    ['client'],
    ['searches'],
    ['search'],
    ['roster'],
    ['activity'],
    ['counts'],
  ],

  /**
   * The Connections tab (T111): the list and its count on the nav, the open connection, the stage
   * list with its numbers, whose networks are in, and the company page's "who works there".
   */
  connection: [
    ['connections'],
    ['connection'],
    ['connectionStages'],
    ['connectionUploads'],
    ['clientConnections'],
    ['counts'],
  ],

  /**
   * A connection moved off the tab (T111): everything above, and where they land — the
   * candidate lists and a Search they were put on, or a company, its contacts and its log.
   */
  connectionMove: [
    ['connections'],
    ['connection'],
    ['connectionStages'],
    ['clientConnections'],
    ['counts'],
    ['candidates'],
    ['candidate'],
    ['candidateEntries'],
    ['search'],
    ['searches'],
    ['roster'],
    ['clients'],
    ['client'],
    ['contact'],
  ],
} as const satisfies Record<string, readonly (readonly unknown[])[]>;
